import React, { FC, useEffect } from 'react'
import { ControllerRenderProps } from 'react-hook-form'
import AutocompleteBase, {
  isAutocompleteBaseOption,
  TInputChangeHandler,
  useAutocompleteInputState,
} from 'transfix-ui/components/AutocompleteBase'
import { useAutocompleteQuery } from 'transfix-ui/components/AutocompleteBase/hooks/useAutocompleteQuery'
import {
  IAutocompleteBaseOption,
  TAutocompleteBaseChangeHandler,
} from 'transfix-ui/components/AutocompleteBase/types'
import { useTrailerAssignmentFacilitiesQuery } from '../graphql/TrailerAssignmentFacilities'

export interface IAutocompleteFacilityProps extends Omit<ControllerRenderProps, 'ref' | 'onBlur'> {
  motorCarrierId: string
  AutocompleteProps?: Omit<
    React.ComponentProps<typeof AutocompleteBase>,
    'options' | 'defaultValue' | 'onChange' | 'value' | 'onBlur'
  >
  dataTest?: string
}

const getComputedValue = (
  options: IAutocompleteBaseOption[],
  value: Pick<IAutocompleteBaseOption, 'id'> | null
): IAutocompleteBaseOption | null => {
  const { id: idProp } = value || {}
  const option = options.find(({ id }) => id === idProp)
  return option || null
}

export const AutocompleteFacility: FC<IAutocompleteFacilityProps> = ({
  AutocompleteProps = {},
  motorCarrierId,
  onChange,
  value: valueProp,
  dataTest,
}) => {
  const [inputValue, setInputValue] = useAutocompleteInputState('')
  const { options = [], loading } = useAutocompleteQuery(
    useTrailerAssignmentFacilitiesQuery,
    {
      variables: { motorCarrierId, searchTerm: inputValue },
    },
    {
      value: valueProp || null,
      getOptionsConfig: data => {
        const { trailerAssignmentFacilities: result } = data || {}
        const { nodes: facilities } = result || {}

        return {
          options: facilities || [],
          groupIndex: 1,
          optionType: 'withIconAndDescription',
          mapOption: facility => {
            const { facility: info, shipperApplication, uuid } = facility || {}
            const { name: shipperName } = shipperApplication || {}
            const { location, name } = info || {}
            const { address, addressLine2, city, stateCode } = location || {}

            return {
              id: uuid,
              text: `${shipperName} - ${name} - ${address},${
                addressLine2 ? ` ${addressLine2},` : ''
              } ${city}, ${stateCode}`,
            }
          },
        }
      },
    }
  )
  const changeHandler: TAutocompleteBaseChangeHandler = (_, newVal) => {
    if (isAutocompleteBaseOption(newVal)) {
      onChange(newVal)
    } else {
      onChange(null)
    }
  }

  const inputChangeHandler: TInputChangeHandler = (e, newVal) => {
    /**
     * MUI triggers an onChange event with `null` value when
     * trying to press delete/backspace when there's a value
     *
     * Because this is an invalid event, we ignore it
     */
    if (e) {
      setInputValue(e, newVal)
    }
  }

  const value = getComputedValue(options, valueProp)

  /**
   * Set input value by default
   */
  useEffect(() => {
    if (!inputValue) {
      setInputValue({} as any, value?.text || '')
    }
  }, [inputValue, setInputValue, value?.text])

  return (
    <AutocompleteBase
      {...AutocompleteProps}
      clearOnBlur={false}
      autoSelect={false}
      options={options}
      data-test={dataTest}
      onChange={changeHandler}
      onInputChange={inputChangeHandler}
      inputValue={inputValue}
      value={value}
      loading={loading}
    />
  )
}
